import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Share, Plus, X, Smartphone } from "lucide-react";

const isIos = () =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
const isMac = () => typeof navigator !== "undefined" && /mac/i.test(navigator.platform || navigator.userAgent);

export default function InstallAgentIA() {
  const [deferred, setDeferred] = useState(typeof window !== "undefined" ? window.__deferredInstall || null : null);
  const [installed, setInstalled] = useState(isStandalone());
  const [help, setHelp] = useState(null); // 'ios' | 'menu' | null
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("ia_install_dismissed") === "1"; } catch { return false; }
  });

  // Bascule le manifeste et les métadonnées vers l'app dédiée « Mon Agent IA » pendant l'affichage
  useEffect(() => {
    const manifest = document.querySelector('link[rel="manifest"]');
    const prevM = manifest?.getAttribute("href");
    manifest?.setAttribute("href", "/manifest-ia.webmanifest");
    const apple = document.querySelector('link[rel="apple-touch-icon"]');
    const prevA = apple?.getAttribute("href");
    apple?.setAttribute("href", "/ia-apple-touch.png");
    const title = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevT = title?.getAttribute("content");
    title?.setAttribute("content", "Mon Agent IA");

    const onBip = (e) => { e.preventDefault(); window.__deferredInstall = e; setDeferred(e); };
    const onReady = () => setDeferred(window.__deferredInstall || null);
    const onInstalled = () => { setInstalled(true); setDeferred(null); toast.success("Mon Agent IA est installé 🎉"); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("bip-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("bip-ready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
      if (manifest && prevM) manifest.setAttribute("href", prevM);
      if (apple && prevA) apple.setAttribute("href", prevA);
      if (title && prevT) title.setAttribute("content", prevT);
    };
  }, []);

  if (installed || dismissed) return null;

  const install = async () => {
    const d = deferred || window.__deferredInstall;
    if (d) {
      d.prompt();
      try { await d.userChoice; } catch { /* ignore */ }
      window.__deferredInstall = null;
      setDeferred(null);
      return;
    }
    setHelp(isIos() ? "ios" : "menu");
  };

  const bookmark = () => {
    setHelp(null);
    toast.message(`${isMac() ? "⌘ + D" : "Ctrl + D"} pour ajouter la page à vos favoris — glissez-la ensuite dans votre barre des favoris pour la garder en vue.`, { duration: 7000 });
  };

  const dismiss = () => { setDismissed(true); try { localStorage.setItem("ia_install_dismissed", "1"); } catch { /* ignore */ } };

  return (
    <div className="relative rounded-xl border border-slate-200 bg-slate-50 p-3 mb-4" data-testid="ia-install-card">
      <button onClick={dismiss} className="absolute top-2 right-2 text-slate-400 hover:text-slate-700" aria-label="Fermer" data-testid="ia-install-dismiss"><X size={15} /></button>
      <div className="flex items-center gap-3">
        <img src="/ia-icon-192.png" alt="Mon Agent IA" className="w-10 h-10 rounded-xl shrink-0 shadow-sm" />
        <div className="min-w-0 flex-1 pr-5">
          <p className="font-semibold text-sm text-slate-800 leading-tight">Gardez « Mon Agent IA » à portée de main</p>
          <p className="text-xs text-slate-500">Installez l'app ou ajoutez la page à vos favoris.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        <div className="flex flex-col items-center">
          <button onClick={install} data-testid="ia-install-btn" className="inline-flex items-center gap-1.5 bg-[#E05638] text-white font-semibold text-xs px-3 py-1.5 rounded-full border border-transparent hover:bg-[#c9472d] transition-colors">
            <Smartphone size={13} /> Installer l'application
          </button>
          <span className="text-[10px] text-slate-400 mt-0.5">sur mobile</span>
        </div>
        <div className="flex flex-col items-center">
          <button onClick={bookmark} data-testid="ia-bookmark-btn" className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 font-semibold text-xs px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors">
            <Star size={13} /> Ajouter aux favoris
          </button>
          <span className="text-[10px] text-slate-400 mt-0.5">sur ordinateur</span>
        </div>
      </div>
      {help === "ios" && (
        <div className="mt-3 text-xs bg-white border border-slate-200 rounded-lg p-2.5 flex items-start gap-2 text-slate-600" data-testid="ia-ios-help">
          <Share size={14} className="shrink-0 mt-0.5" />
          <span>Sur iPhone / iPad : touchez <strong>Partager</strong> puis <strong>« Sur l'écran d'accueil »</strong> <Plus size={11} className="inline" />.</span>
        </div>
      )}
      {help === "menu" && (
        <div className="mt-3 text-xs bg-white border border-slate-200 rounded-lg p-2.5 text-slate-600" data-testid="ia-menu-help">
          Ouvrez le <strong>menu de votre navigateur</strong> (⋮) puis choisissez <strong>« Installer l'application »</strong> ou <strong>« Ajouter à l'écran d'accueil »</strong>.
        </div>
      )}
    </div>
  );
}
